import type { Validator } from "remix-validated-form";
import { useLoqateContext } from "../hooks/useLoqate";
import { useErrorAlert } from "@commercekit/react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";

export const useValidateAddress = (
  formRef: React.RefObject<HTMLFormElement>,
  validator: Validator<any>
) => {

  const {
    isVerifying,
    verifyAddress,
    setIsVerifying,
    setSuggestionModalData,
    bypassValidation,
    setBypassValidation,
    suggestionModalData,
  } = useLoqateContext();
  const { show } = useErrorAlert();
  const { $pt } = usePrismicLabels();

  const validateAddress = async (): Promise<boolean> => {
    if (bypassValidation) return true;
    const formElement = formRef.current;
    if (!formElement) return false;

    const formData = new FormData(formElement);
    const { error } = await validator.validate(formData as any);
    if (error) return false;

    const postalCode = formData.get("postalCode")?.toString() ?? "";
    const city = formData.get("city")?.toString() ?? "";
    const address1 = formData.get("address1")?.toString() ?? "";
    const houseNumber = formData.get("houseNumber")?.toString() ?? "";
    const houseNumberAddition =
      formData.get("houseNumberExt")?.toString() ?? "";
    const countryCode = formData.get("countryCode")?.toString() ?? "";

    const addresses = [
      {
        Country: countryCode,
        PostalCode: postalCode,
        Locality: city,
        Address: `${address1} ${houseNumber} ${houseNumberAddition}`,
      },
    ];

    const addressRatings = await verifyAddress(addresses);

    if (!addressRatings) {
      show($pt("generic_invalid_loqate_address"), "danger");
      return false;
    }

    const addressesWithSuggestions = [];
    let hasLowScore = false;

    for (const [_, rating] of addressRatings.entries()) {
      if (rating.score <= 79 || !rating.isVerified) {
        // Enhanced error message for low scores
        const message = $pt("generic_invalid_loqate_address")
        show(message, "danger");
        hasLowScore = true;
      } else if (
        rating.score >= 80 &&
        rating.score <= 92 &&
        rating.suggestions &&
        rating.suggestions.length > 0
      ) {
        addressesWithSuggestions.push(rating);
      }
    }

    // If any address has a low score, stop immediately
    if (hasLowScore) {
      return false;
    }

    // If we have addresses with medium scores and suggestions, show modal
    if (addressesWithSuggestions.length > 0) {
      // Always pass array - modal will check length to determine display logic
      setSuggestionModalData(addressesWithSuggestions);
      return false;
    }

    // If we got here but had medium scores without suggestions, still return false
    if (addressRatings.some((r) => r.score >= 80 && r.score <= 92)) {
      return false;
    }

    return true;
  };

  return {
    validateAddress,
    isVerifying,
    setIsVerifying,
    suggestionModalData,
    setSuggestionModalData,
    bypassValidation,
    setBypassValidation,
  };
};